import {
  ClassConstructor, Exclude, instanceToPlain,
  plainToInstance,
  Type
} from 'class-transformer'
import i18n from 'vue-i18n'

import { SocketsActionsTypes } from '../../../../../components'
import { BaseNodeModelData, IBaseData } from '../../../BaseNode/BaseNodeDataModel'
import { BaseNodeModel } from '../../../BaseNode/BaseNodeModel'
import { CustomNode, CustomNodeComponent, NodeTypes } from '../../../index'
import SampleActionNode from '../ui/SampleNode/SampleActionNode.vue'
import ActionNodeSidebar from '../ui/sidebar/ActionNodeSidebar.vue'
import ActionNodeAction, { ActionNodeActionProxy } from './Actions'
import { KickAction } from './Actions/KickAction/KickAction'
import { KickActionProxy } from './Actions/KickAction/KickActionProxy'
import {
  AddLabelAction,
  LabelsAction,
  RemoveLabelAction
} from './Actions/LabelsAction/LabelsAction'
import { LabelsActionProxy } from './Actions/LabelsAction/LabelsActionProxy'
import { NotifyGroupAction } from './Actions/NotifyGroupAction/NotifyGroupAction'
import { NotifyGroupActionProxy } from './Actions/NotifyGroupAction/NotifyGroupActionProxy'
import { NotifyUsersAction } from './Actions/NotifyUsersAction/NotifyUsersAction'
import { NotifyUsersActionProxy } from './Actions/NotifyUsersAction/NotifyUsersActionProxy'
import { PropertyAction } from './Actions/PropertyAction/PropertyAction'
import { PropertyActionProxy } from './Actions/PropertyAction/PropertyActionProxy'
import { RemoveProperties } from './Actions/RemoveProperties/RemoveProperties'
import { RemovePropertiesProxy } from './Actions/RemoveProperties/RemovePropertiesProxy'
import { ActionTypes } from './Actions/types'
import { ActionNodeProps } from './types'

export default class ActionNodeBuilder extends CustomNodeComponent {
  sidebarComponent = ActionNodeSidebar

  constructor(i18n: i18n, nodeProps: ActionNodeProps) {
    super(SampleActionNode, ActionNode, NodeTypes.Action, i18n, nodeProps)
  }

  getBaseNodeModel = ([ x, y ]: [ number, number ]) => {
    return {
      data: plainToInstance(ActionNodeData, {
        type: NodeTypes.Action,
        actions: []
      }),
      color: null,
      output: CustomNode.LastSocketId += 1,
      input: CustomNode.LastSocketId += 1,
      x,
      y,
    }
  }
}

export class ActionNode extends CustomNode {
  data: ActionNodeDataProxy
  declare nodeProps: ActionNodeProps

  constructor(name: NodeTypes.Action, model: BaseNodeModel, nodeProps: ActionNodeProps) {
    super(name, model, nodeProps)

    if (model.data instanceof ActionNodeData) {
      this.data = new ActionNodeDataProxy (model.data, this.socketActions, nodeProps)
    } else {
      throw new Error('To node come unknown data')
    }
  }
}

export class ActionNodeDataProxy implements IBaseData {
  type: NodeTypes.Action = NodeTypes.Action
  actions: Array<ActionNodeActionProxy> = []

  @Exclude()
  private readonly nodeProps: ActionNodeProps
  @Exclude()
  private readonly socketsActions: SocketsActionsTypes

  constructor(data: ActionNodeData, socketsActions: SocketsActionsTypes, nodeProps: ActionNodeProps) {
    this.socketsActions = socketsActions
    this.nodeProps = nodeProps

    data.actions.forEach((a) => {
      if (a instanceof PropertyAction) {
        this.actions.push(new PropertyActionProxy(a, nodeProps))
      } else if (a instanceof LabelsAction) {
        this.actions.push(new LabelsActionProxy(a))
      } else if (a instanceof NotifyGroupAction) {
        this.actions.push(new NotifyGroupActionProxy(a, socketsActions))
      } else if (a instanceof NotifyUsersAction) {
        this.actions.push(new NotifyUsersActionProxy(a, socketsActions))
      } else if (a instanceof RemoveProperties) {
        this.actions.push(new RemovePropertiesProxy(a))
      } else if (a instanceof KickAction) {
        this.actions.push(new KickActionProxy(a))
      }

      if (this.nodeProps?.externalActions && this.nodeProps?.externalActions.length) {
        this.nodeProps.externalActions.forEach(e => {
          if (a instanceof e.action) {
            this.actions.push(new e.proxy(a, nodeProps))
          }
        })
      }
    })
  }

  getData(): BaseNodeModelData {
    return plainToInstance(ActionNodeData, {
      type: NodeTypes.Action,
      actions: instanceToPlain(this.actions.map((a) => a.getModel()))
    })
  }

  addAction(type: ActionTypes): void {
    switch (type) {
      case ActionTypes.AddLabel:
        this.actions.push(new LabelsActionProxy(new AddLabelAction().getNewModel()))
        break
      case ActionTypes.RemoveLabel:
        this.actions.push(new LabelsActionProxy(new RemoveLabelAction().getNewModel()))
        break
      case ActionTypes.NotifyGroup:
        this.actions.push(new NotifyGroupActionProxy(new NotifyGroupAction().getNewModel(), this.socketsActions))
        break
      case ActionTypes.NotifyUsers:
        this.actions.push(new NotifyUsersActionProxy(new NotifyUsersAction().getNewModel(), this.socketsActions))
        break
      case ActionTypes.Property:
        this.actions.push(new PropertyActionProxy(new PropertyAction().getNewModel(), this.nodeProps))
        break
      case ActionTypes.RemoveProperties:
        this.actions.push(new RemovePropertiesProxy(new RemoveProperties().getNewModel()))
        break
      case ActionTypes.Kick:
        this.actions.push(new KickActionProxy(new KickAction().getNewModel()))
        break

    }

    if (this.nodeProps?.externalActions && this.nodeProps?.externalActions.length) {
      this.nodeProps.externalActions.forEach(a => {
        if (type === a.type) {
          this.actions.push(new a.proxy(new a.action().getNewModel(), this.nodeProps))
        }
      })
    }
  }

  removeAction(proxy: ActionNodeActionProxy): void {
    this.actions.splice(this.actions.indexOf(proxy), 1)
  }
}

export class ActionNodeActionTypesFactory {
  static ModelsFactories: Array<{name: ActionTypes | string, value: ClassConstructor<ActionNodeAction>}> = [
    { name: ActionTypes.Property, value: PropertyAction },
    { name: ActionTypes.RemoveProperties, value: RemoveProperties },
    { name: ActionTypes.AddLabel, value: AddLabelAction },
    { name: ActionTypes.RemoveLabel, value: RemoveLabelAction },
    { name: ActionTypes.NotifyUsers, value: NotifyUsersAction },
    { name: ActionTypes.NotifyGroup, value: NotifyGroupAction },
    { name: ActionTypes.Kick, value: KickAction },
  ]

  static SetModelFactory(name: string, value: ClassConstructor<ActionNodeAction>): void {
    ActionNodeActionTypesFactory.ModelsFactories.push({ name, value })
  }
}

export class ActionNodeData extends BaseNodeModelData {
  declare type: NodeTypes.Action
  @Type(() => ActionNodeAction, {
    keepDiscriminatorProperty: true,
    discriminator: {
      property: 'type',
      subTypes: ActionNodeActionTypesFactory.ModelsFactories
    }
  })
  actions!: Array<ActionNodeAction>
}
